import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa";
import { ArrowUpRight, Check, Gift, LogOut } from "lucide-react";
import { translations, CONTACT } from "@/content";
import { trackEvent } from "@/analytics";
import { useAuth } from "@/auth/AuthContext";
import { Overline, SerifHeading, FadeUp } from "@/primitives";

const readLang = () => (window.localStorage.getItem("nunu_lang") === "zh" ? "zh" : "en");

export default function Account() {
  const { user, loading, logout, savePreference } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [lang, setLang] = useState(readLang);
  const [saving, setSaving] = useState(null);
  const t = useMemo(() => translations[lang], [lang]);
  const resolved = user || location.state?.user;

  useEffect(() => {
    window.localStorage.setItem("nunu_lang", lang);
    document.documentElement.lang = lang === "zh" ? "zh-CN" : "en";
  }, [lang]);

  useEffect(() => {
    if (!loading && !resolved) navigate("/", { replace: true });
  }, [loading, resolved, navigate]);

  if (!resolved) {
    return (
      <div data-testid="account-loading" className="min-h-screen bg-sand flex items-center justify-center">
        <span className="font-serif-display italic text-2xl text-matcha animate-pulse">Nunu</span>
      </div>
    );
  }

  const tier = t.services.tiers.find((x) => x.duration.startsWith(resolved.preferred_tier || "")) || null;
  const tierLabel = resolved.preferred_tier ? (tier?.duration || `${resolved.preferred_tier} min`) : "";
  const waText = encodeURIComponent(
    t.account.waMessage
      .replace("{name}", resolved.name)
      .replace("{tier}", tierLabel || t.account.anyTier)
      .replace("{code}", resolved.gift_code),
  );
  const waUrl = `${CONTACT.whatsappUrl}?text=${waText}`;

  const choose = async (minutes) => {
    setSaving(minutes);
    await savePreference(minutes);
    trackEvent("preference_saved", { tier: minutes });
    setSaving(null);
  };

  const onLogout = async () => {
    trackEvent("logout");
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div data-testid="account-page" className="min-h-screen bg-sand text-ink">
      <header className="border-b border-clay/60">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="account-logo" className="flex items-center gap-2">
            <span className="w-2 h-2 bg-matcha rounded-full" />
            <span className="font-serif-display text-xl tracking-tight">
              Nunu <em className="italic text-matcha">Massage</em>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex items-center text-[12px] font-sans-body border border-clay/80 rounded-full overflow-hidden">
              <button
                data-testid="account-lang-en"
                onClick={() => setLang("en")}
                className={`px-3 py-1.5 transition-colors ${lang === "en" ? "bg-ink text-sand" : "text-ink-soft"}`}
              >
                EN
              </button>
              <button
                data-testid="account-lang-zh"
                onClick={() => setLang("zh")}
                className={`px-3 py-1.5 transition-colors ${lang === "zh" ? "bg-ink text-sand" : "text-ink-soft"}`}
              >
                中文
              </button>
            </div>
            <button
              type="button"
              onClick={onLogout}
              data-testid="account-logout"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink-soft hover:text-ink transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              {t.account.signOut}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <FadeUp>
          <div className="flex items-center gap-5">
            {resolved.picture && (
              <img
                src={resolved.picture}
                alt=""
                referrerPolicy="no-referrer"
                className="w-14 h-14 rounded-full object-cover ring-1 ring-clay"
              />
            )}
            <div>
              <Overline>— {t.account.overline}</Overline>
              <SerifHeading as="h1" className="mt-2 text-[40px] md:text-[64px]">
                <span data-testid="account-greeting">
                  {t.account.hello}, {resolved.name.split(" ")[0]}.
                </span>
              </SerifHeading>
              <p data-testid="account-email" className="mt-2 text-ink-soft text-[13px]">
                {resolved.email}
              </p>
            </div>
          </div>
        </FadeUp>

        <div className="mt-14 grid md:grid-cols-12 gap-8">
          <FadeUp delay={0.1} className="md:col-span-5">
            <div data-testid="gift-card" className="bg-ink text-sand p-8 md:p-10 h-full flex flex-col justify-between">
              <div className="flex items-center gap-3 text-clay">
                <Gift className="w-5 h-5" />
                <span className="text-[11px] tracking-[0.28em] uppercase">{t.account.giftLabel}</span>
              </div>
              <div className="mt-10">
                <div className="font-serif-display text-[56px] md:text-[64px] leading-none text-sand">¥100</div>
                <p className="mt-3 text-sand/70 text-[14px] leading-relaxed max-w-xs">{t.account.giftSub}</p>
              </div>
              <div className="mt-8 border-t border-sand/15 pt-6">
                <div className="text-[10px] tracking-[0.28em] uppercase text-sand/50">{t.account.codeLabel}</div>
                <div data-testid="gift-code" className="mt-2 font-serif-display text-3xl tracking-wide text-clay">
                  {resolved.gift_code}
                </div>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.2} className="md:col-span-7">
            <div className="border border-clay/70 p-8 md:p-10 h-full">
              <Overline>— {t.account.prefLabel}</Overline>
              <h2 className="mt-3 font-serif-display text-3xl md:text-4xl">{t.account.prefTitle}</h2>
              <p className="mt-3 text-ink-soft text-[14px]">{t.account.prefSub}</p>
              <div className="mt-8 grid sm:grid-cols-3 gap-3">
                {t.services.tiers.map((tr) => {
                  const minutes = tr.duration.split(" ")[0];
                  const active = resolved.preferred_tier === minutes;
                  return (
                    <motion.button
                      key={minutes}
                      type="button"
                      whileTap={{ scale: 0.98 }}
                      data-testid={`pref-tier-${minutes}`}
                      onClick={() => choose(minutes)}
                      disabled={saving !== null}
                      className={`text-left p-5 border transition-colors duration-300 ${
                        active ? "bg-matcha text-sand border-matcha" : "border-clay/70 hover:border-matcha"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] tracking-[0.22em] uppercase opacity-70">{tr.duration}</span>
                        {active && <Check className="w-4 h-4" />}
                      </div>
                      <div className="mt-3 font-serif-display text-2xl">{tr.name}</div>
                      <div className="mt-1 font-serif-display text-xl opacity-80">{tr.price}</div>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          </FadeUp>
        </div>

        <FadeUp delay={0.3} className="mt-8">
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="account-whatsapp-cta"
            onClick={() => trackEvent("booking_intent", { channel: "whatsapp", source: "account", tier: resolved.preferred_tier })}
            className="group flex items-center justify-between gap-4 bg-whatsapp text-white px-6 md:px-8 py-6 hover:brightness-95 transition-all"
          >
            <span className="flex items-center gap-4">
              <FaWhatsapp className="w-6 h-6" />
              <span>
                <span className="block text-[12px] tracking-[0.22em] uppercase">{t.account.bookCta}</span>
                <span className="block mt-1 text-white/80 text-[13px] normal-case tracking-normal">
                  {t.account.bookHint}
                </span>
              </span>
            </span>
            <ArrowUpRight className="w-5 h-5 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
          </a>
        </FadeUp>

        <FadeUp delay={0.4} className="mt-10">
          <Link
            to="/"
            data-testid="account-back-home"
            className="text-[11px] tracking-[0.22em] uppercase text-ink-soft border-b border-ink-soft/40 pb-1 hover:text-matcha hover:border-matcha transition-colors"
          >
            {t.account.backHome}
          </Link>
        </FadeUp>
      </main>
    </div>
  );
}
